/**
 * VoiceProfilePage/index.tsx
 *
 * Let users paste up to 3 LinkedIn post URLs they've written.
 * The system extracts the full text automatically via POST /api/voice/fetch-url
 * (Jina AI Reader → og:description fallback) and stores it for RAG retrieval
 * so generated posts sound more like the user.
 *
 * Rules:
 *   • Max 3 preferred posts
 *   • Extracted text is read-only (not editable after extraction)
 *   • Posts can be deleted individually
 *
 * Rollback:
 *   Remove the /voice-profile <Route> in routes/index.tsx and the nav link
 *   in UserDashboard DashNav. Zero DB changes — the voice_post_library table
 *   already exists from migration 040.
 */

import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  pageStyle, navStyle, card, primaryBtn, secondaryBtn,
  ACCENT, ACCENT_08, ACCENT_12, spinnerStyle,
} from "../DistillPageV2/sharedStyles";
import {
  getVoiceSettingsApi,
  addPreferredPostApi,
  deletePreferredPostApi,
  fetchPostFromUrlApi,
  type PreferredPost,
} from "../../api/voiceApi";

// ── Constants ─────────────────────────────────────────────────────────────────

const MAX_POSTS = 3;

// ── Helpers ───────────────────────────────────────────────────────────────────

function isLinkedInUrl(url: string): boolean {
  try {
    const u = new URL(url);
    return u.hostname.includes("linkedin.com");
  } catch {
    return false;
  }
}

function shortDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return "";
  }
}

// ── Sub-component: single preferred post card ─────────────────────────────────

interface PostCardProps {
  post:     PreferredPost;
  onDelete: (id: number) => void;
}

const PostCard: React.FC<PostCardProps> = ({ post, onDelete }) => {
  const [expanded,  setExpanded]  = useState(false);
  const [deleting,  setDeleting]  = useState(false);

  const handleDelete = async () => {
    if (!window.confirm("Remove this post from your voice profile?")) return;
    setDeleting(true);
    try {
      await deletePreferredPostApi(post.id);
      onDelete(post.id);
    } catch {
      setDeleting(false);
    }
  };

  return (
    <div style={{
      ...card,
      padding: "16px 18px",
      display: "flex",
      flexDirection: "column",
      gap: "10px",
      opacity: deleting ? 0.5 : 1,
      transition: "opacity 0.2s",
    }}>
      {/* Header row */}
      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {/* Excerpt (first line) */}
          <div style={{
            fontSize: "14px", fontWeight: 700, color: "#1a1a1a",
            lineHeight: "1.4",
            overflow: "hidden", textOverflow: "ellipsis",
            display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" as const,
          }}>
            {post.excerpt}
          </div>

          {/* Source URL link */}
          {post.source_url && (
            <a
              href={post.source_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                fontSize: "12px", color: "#5B4FE8",
                textDecoration: "none", marginTop: "4px", display: "block",
                overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              }}
            >
              ↗ {post.source_url}
            </a>
          )}

          {/* Date */}
          <div style={{ fontSize: "11px", color: "#aaa", marginTop: "3px" }}>
            Added {shortDate(post.created_at)}
          </div>
        </div>

        {/* Delete button */}
        <button
          onClick={handleDelete}
          disabled={deleting}
          title="Remove this post"
          style={{
            background: "none", border: "none",
            color: "#ccc", cursor: "pointer",
            fontSize: "16px", padding: "0 2px",
            lineHeight: 1, flexShrink: 0,
            transition: "color 0.15s",
          }}
          onMouseEnter={e => ((e.target as HTMLElement).style.color = "#E84F4F")}
          onMouseLeave={e => ((e.target as HTMLElement).style.color = "#ccc")}
        >
          ×
        </button>
      </div>

      {/* Expand/collapse full text toggle */}
      <button
        onClick={() => setExpanded(x => !x)}
        style={{
          background: "none", border: "none",
          color: ACCENT, fontSize: "12px", fontWeight: 700,
          cursor: "pointer", padding: 0, fontFamily: "inherit",
          textAlign: "left", alignSelf: "flex-start",
        }}
      >
        {expanded ? "Hide full text ↑" : "Show full text ↓"}
      </button>

      {/* Full text (read-only) */}
      {expanded && (
        <div style={{
          background: "#FAFAF9",
          border: "1.5px solid #E4E4E0",
          borderRadius: "10px",
          padding: "14px 16px",
          fontSize: "13px",
          lineHeight: "1.7",
          color: "#444",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          userSelect: "text",
        }}>
          {post.excerpt}
          {/* Note: excerpt is stored from the backend; for full text we'd need
              a separate /api/voice/preferred-posts/:id endpoint. For Phase 3
              the excerpt is sufficient to show context. */}
        </div>
      )}
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const VoiceProfilePage: React.FC = () => {
  const [posts,       setPosts]       = useState<PreferredPost[]>([]);
  const [loading,     setLoading]     = useState(true);
  const [loadError,   setLoadError]   = useState<string | null>(null);

  const [url,         setUrl]         = useState("");
  const [extracting,  setExtracting]  = useState(false);
  const [extractError, setExtractError] = useState<string | null>(null);

  const urlInputRef = useRef<HTMLInputElement>(null);

  // ── Load on mount ─────────────────────────────────────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const data = await getVoiceSettingsApi();
        setPosts(data.preferredPosts);
      } catch (err: any) {
        const status = err?.response?.status;
        if (status === 401) {
          setLoadError("Sign in to manage your voice profile.");
        } else {
          setLoadError("Couldn't load your voice profile. Try refreshing.");
        }
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ── Extract + save from URL ───────────────────────────────────────────────
  const handleExtract = async () => {
    const trimmed = url.trim();
    if (!trimmed) return;

    if (!isLinkedInUrl(trimmed)) {
      setExtractError("Please paste a LinkedIn post URL (linkedin.com/...)");
      return;
    }
    if (posts.length >= MAX_POSTS) {
      setExtractError(`You already have ${MAX_POSTS} posts. Remove one before adding another.`);
      return;
    }

    setExtracting(true);
    setExtractError(null);

    try {
      // Step 1: extract text
      const result = await fetchPostFromUrlApi(trimmed);

      if (!result.success || !result.text) {
        setExtractError(
          "Couldn't extract the post text. Make sure the URL is a public LinkedIn post."
        );
        setExtracting(false);
        return;
      }

      // Step 2: save as preferred post
      const saved = await addPreferredPostApi({ text: result.text, url: trimmed });

      // Step 3: append to local list (use the excerpt from the server response)
      const newPost: PreferredPost = {
        id:               saved.id,
        excerpt:          saved.excerpt,
        source_url:       saved.source_url,
        engagement_score: 5.0,
        created_at:       new Date().toISOString(),
      };
      setPosts(prev => [newPost, ...prev]);
      setUrl("");

      if (urlInputRef.current) urlInputRef.current.focus();
    } catch (err: any) {
      const msg = err?.response?.data?.error ?? "Something went wrong. Try again.";
      setExtractError(msg);
    } finally {
      setExtracting(false);
    }
  };

  const handleDelete = (id: number) => {
    setPosts(prev => prev.filter(p => p.id !== id));
  };

  const canAdd = posts.length < MAX_POSTS;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={pageStyle}>

      {/* ── Nav ─────────────────────────────────────────────────────────────── */}
      <nav style={navStyle}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <Link
            to="/dashboard"
            style={{
              display: "inline-flex", alignItems: "center", gap: 6,
              textDecoration: "none", color: ACCENT, background: "#fff",
              border: `1.5px solid ${ACCENT}`, borderRadius: 8,
              padding: "6px 12px", fontSize: 13, fontWeight: 700,
              fontFamily: "inherit",
            }}
          >
            <span style={{ fontSize: 15, lineHeight: 1 }}>←</span>
            Dashboard
          </Link>
          <Link to="/distill-v2" style={{ textDecoration: "none", color: "inherit" }}>
            <span style={{ fontFamily: "'Bricolage Grotesque', sans-serif", fontWeight: 800, fontSize: 18 }}>
              distill
              <sup style={{
                fontSize: 8, letterSpacing: "0.1em", fontWeight: 600,
                color: ACCENT, verticalAlign: "super", marginLeft: 2,
                textTransform: "uppercase" as const,
              }}>
                v2
              </sup>
            </span>
          </Link>
          <span style={{ color: "#E4E4E0", fontWeight: 300 }}>|</span>
          <span style={{ fontSize: 14, fontWeight: 700, color: "#444" }}>Voice Profile</span>
        </div>
      </nav>

      {/* ── Main ────────────────────────────────────────────────────────────── */}
      <main style={{ maxWidth: 640, margin: "0 auto", padding: "48px 24px 80px" }}>

        {/* Heading */}
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontSize: 28, fontWeight: 800, letterSpacing: "-0.03em", margin: "0 0 8px" }}>
            Your Voice Profile
          </h1>
          <p style={{ fontSize: 14, color: "#888", margin: 0, lineHeight: 1.6 }}>
            Paste up to {MAX_POSTS} LinkedIn posts you've written.
            We extract the text and use it to match your writing style when generating new posts.
          </p>
        </div>

        {/* ── URL input card ─────────────────────────────────────────────────── */}
        <div style={{ ...card, marginBottom: 24 }}>
          <div style={{ fontSize: "13px", fontWeight: 700, marginBottom: "10px", color: "#444" }}>
            Add a LinkedIn post URL
            <span style={{
              marginLeft: 8, fontSize: 11, fontWeight: 600,
              color: canAdd ? "#aaa" : "#E84F4F",
            }}>
              {posts.length}/{MAX_POSTS} used
            </span>
          </div>

          <div style={{ display: "flex", gap: "8px" }}>
            <input
              ref={urlInputRef}
              type="url"
              placeholder="https://www.linkedin.com/posts/..."
              value={url}
              onChange={e => { setUrl(e.target.value); setExtractError(null); }}
              onKeyDown={e => {
                if (e.key === "Enter" && !extracting && canAdd) handleExtract();
              }}
              disabled={extracting || !canAdd}
              style={{
                flex: 1,
                border: "1.5px solid #E4E4E0",
                borderRadius: "10px",
                padding: "10px 14px",
                fontSize: "14px",
                fontFamily: "inherit",
                outline: "none",
                color: "#0A0A0A",
                background: canAdd ? "#fff" : "#FAFAF9",
                transition: "border-color 0.15s",
              }}
              onFocus={e => (e.target.style.borderColor = ACCENT)}
              onBlur={e  => (e.target.style.borderColor = "#E4E4E0")}
            />
            <button
              onClick={handleExtract}
              disabled={extracting || !canAdd || !url.trim()}
              style={{
                ...primaryBtn,
                opacity: (extracting || !canAdd || !url.trim()) ? 0.5 : 1,
                cursor:  (extracting || !canAdd || !url.trim()) ? "not-allowed" : "pointer",
                flexShrink: 0,
                padding: "10px 18px",
              }}
            >
              {extracting
                ? <><span style={spinnerStyle} /> Extracting…</>
                : "Extract →"
              }
            </button>
          </div>

          {/* Inline error */}
          {extractError && (
            <div style={{
              marginTop: "10px",
              fontSize: "13px",
              color: "#C0392B",
              background: "#FFF0F0",
              border: "1.5px solid #E84F4F",
              borderRadius: "8px",
              padding: "8px 12px",
            }}>
              {extractError}
            </div>
          )}

          {/* Cap reached */}
          {!canAdd && !extractError && (
            <div style={{
              marginTop: "10px", fontSize: "12px", color: "#888",
              background: ACCENT_08, borderRadius: "8px", padding: "8px 12px",
            }}>
              You've reached the {MAX_POSTS}-post limit. Remove a post below to add a new one.
            </div>
          )}

          {/* Helper hint */}
          {canAdd && (
            <div style={{ marginTop: "8px", fontSize: "11px", color: "#bbb", lineHeight: 1.5 }}>
              Only public LinkedIn post URLs are supported. The post text is extracted automatically — no copy-pasting needed.
            </div>
          )}
        </div>

        {/* ── Posts list ─────────────────────────────────────────────────────── */}
        {loading && (
          <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "20px 0", color: "#888" }}>
            <span style={spinnerStyle} />
            <span style={{ fontSize: 13 }}>Loading your voice profile…</span>
          </div>
        )}

        {loadError && !loading && (
          <div style={{
            background: "#FFF0F0", border: "1.5px solid #E84F4F",
            borderRadius: "12px", padding: "14px 16px",
            fontSize: "14px", color: "#C0392B",
          }}>
            {loadError}
          </div>
        )}

        {!loading && !loadError && posts.length === 0 && (
          <div style={{
            textAlign: "center", padding: "40px 0",
            color: "#aaa", fontSize: "14px", lineHeight: 1.7,
          }}>
            <div style={{ fontSize: 32, marginBottom: 10 }}>✍️</div>
            <div style={{ fontWeight: 700, color: "#555", marginBottom: 4 }}>
              No posts yet
            </div>
            <div>
              Paste a URL above to teach the AI your writing style.
            </div>
          </div>
        )}

        {!loading && !loadError && posts.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{
              fontSize: "12px", fontWeight: 700,
              textTransform: "uppercase" as const,
              letterSpacing: "0.06em", color: "#888",
              marginBottom: 4,
            }}>
              Your Style References
            </div>
            {posts.map(post => (
              <PostCard key={post.id} post={post} onDelete={handleDelete} />
            ))}
          </div>
        )}

        {/* ── Footer info ────────────────────────────────────────────────────── */}
        {!loading && (
          <div style={{
            marginTop: 40,
            padding: "16px 18px",
            background: ACCENT_08,
            border: `1.5px solid ${ACCENT_12}`,
            borderRadius: "12px",
            fontSize: "13px",
            color: "#555",
            lineHeight: 1.6,
          }}>
            <strong style={{ color: "#333" }}>How this works</strong><br />
            When you generate a post, we find the most similar post from your voice profile
            and show it to the AI as a style example — same rhythm, same stance, same way of
            landing the final line. The AI doesn't copy phrases; it mirrors the pattern.
          </div>
        )}

      </main>
    </div>
  );
};

export default VoiceProfilePage;
